import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { Button } from '@/components/ui/Button';
import { StatusChip } from '@/components/ui/StatusChip';
import { formatDate } from '@/lib/organization';

import { arcStatus, type Arc } from './arcTypes';

interface ArcDetailsRowProps {
  arc: Arc;
  readOnly: boolean;
  onEdit: () => void;
  /** Asks to take the ARC out of the app; offered only while it is published (D8). */
  onUnpublish: () => void;
}

/**
 * The ARC's name, its status, and when it was last published, in one row as
 * the wireframe draws it (Part 2, page 4); on a phone the four wrap. While
 * the ARC is published, Unpublish ARC sits next to the status it changes
 * (group 6, D8, B13).
 */
export function ArcDetailsRow({
  arc,
  readOnly,
  onEdit,
  onUnpublish,
}: ArcDetailsRowProps) {
  const status = arcStatus(arc.status);
  const published = arc.status === 'PUBLISHED';
  return (
    <Paper
      variant="outlined"
      data-testid="arc-builder-details-card"
      sx={{
        px: 3,
        py: 2.5,
        display: 'flex',
        columnGap: 3,
        rowGap: 1.5,
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography
        variant="subtitle1"
        component="h2"
        data-testid="arc-builder-title"
        sx={{ fontWeight: 600, overflowWrap: 'anywhere', flex: '1 1 12rem', minWidth: 0 }}
      >
        {arc.title}
      </Typography>
      <StatusChip
        label={status.label}
        tone={status.tone}
        filled
        testId="arc-builder-status-chip"
      />
      <Typography
        variant="body2"
        data-testid="arc-builder-published-value"
        sx={{ textAlign: 'center', lineHeight: 1.3 }}
      >
        {arc.published_at ? (
          <>
            Last published{' '}
            <Box component="span" sx={{ display: 'block' }}>
              {formatDate(arc.published_at)}
            </Box>
          </>
        ) : (
          'Not published yet'
        )}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {published ? (
          <Button
            variant="secondary"
            onClick={onUnpublish}
            disabled={readOnly}
            data-testid="arc-builder-unpublish-button"
          >
            Unpublish ARC
          </Button>
        ) : null}
        <Button
          variant="secondary"
          onClick={onEdit}
          disabled={readOnly}
          data-testid="arc-builder-edit-details-button"
        >
          Edit ARC Details
        </Button>
      </Box>
    </Paper>
  );
}
